import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { ExcelGrid } from '@/components/excelGrid/ExcelGrid'
import { type ExcelColumnDef, getColHeader, getColVal } from '@/components/excelGrid/types'
import { exportToCsv } from '@/components/excelGrid/tsvUtils'
import {
  fetchReservationsApi,
  createManualReservationApi,
  updateReservationQtyApi,
  releaseReservationApi,
  type ReservationItem,
} from '@/services/warehouse/reservationsApi'
import { getStockOverview } from '@/services/inventory/apiClient'
import type { MaterialStockOverviewItem } from '@/services/inventory/types'
import { useContext } from 'react'
import { AuthContext } from '@/features/auth/AuthContext'
import { formatDateTime, formatDateTimeFull } from '@/lib/dateFormat'
import { MaterialTypeahead } from '@/components/forms/MaterialTypeahead'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'

function useSafeAuth() {
  return useContext(AuthContext)
}

function fmtN(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '0'
  const num = typeof n === 'number' ? n : Number(n)
  if (!Number.isFinite(num)) return '0'
  return num.toLocaleString('vi-VN', { maximumFractionDigits: 2 })
}

export interface ReservationsPageProps {
  initialItems?: ReservationItem[]
  initialTotal?: number
  initialStatusCounts?: { active: number; consumed: number; released: number; total: number }
  initialStatusFilter?: string
}

export function ReservationsPage({
  initialItems,
  initialStatusFilter,
}: ReservationsPageProps = {}) {
  const [searchParams] = useSearchParams()
  const auth = useSafeAuth()
  const canReservationsWrite = auth
    ? auth.can('reservations.write') ||
      auth.can('inventory.write') ||
      (auth.user?.roles ? auth.user.roles.some((r) => r === 'WAREHOUSE' || r === 'PURCHASING') : false)
    : true

  const initialStatus = initialStatusFilter ?? (searchParams.get('status') || (initialItems ? 'all' : 'ACTIVE'))
  const initialSource = searchParams.get('source') || 'all'
  const initialCompletedLsx = searchParams.get('completed_lsx') === '1' || searchParams.get('completed_lsx') === 'true'

  const [statusFilter, setStatusFilter] = useState<string>(initialStatus)
  const [sourceFilter, setSourceFilter] = useState<string>(initialSource)
  const [completedLsxOnly, setCompletedLsxOnly] = useState<boolean>(initialCompletedLsx)

  const [items, setItems] = useState<ReservationItem[]>(initialItems ?? [])
  const [loading, setLoading] = useState(!initialItems)
  const [searchQuery, setSearchQuery] = useState('')
  const [resetWidthsCounter, setResetWidthsCounter] = useState(0)
  const [toastMessage, setToastMessage] = useState<string | null>(null)

  const [materials, setMaterials] = useState<MaterialStockOverviewItem[]>([])

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false)
  const [editSnapshot, setEditSnapshot] = useState<ReservationItem[] | null>(null)
  const [draftQtys, setDraftQtys] = useState<Record<string, number>>({})
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [confirmReason, setConfirmReason] = useState('')

  // Hold modal state
  const [holdModalOpen, setHoldModalOpen] = useState(false)
  const [holdMaterialId, setHoldMaterialId] = useState('')
  const [holdPlanLineId, setHoldPlanLineId] = useState('')
  const [holdQty, setHoldQty] = useState<number | string>('')
  const [holdNote, setHoldNote] = useState('')

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg)
    setTimeout(() => setToastMessage(null), 3500)
  }, [])

  const loadData = useCallback(async () => {
    if (initialItems) return
    setLoading(true)
    try {
      const [resData, matData] = await Promise.all([
        fetchReservationsApi({
          status: statusFilter === 'all' ? 'all' : statusFilter,
          source: sourceFilter !== 'all' ? sourceFilter : undefined,
          completed_lsx: completedLsxOnly || undefined,
        }),
        getStockOverview().catch(() => []),
      ])
      setItems(resData.data || resData.items || [])
      setMaterials(matData)
    } catch {
      showToast('Lỗi khi tải danh sách giữ hàng')
    } finally {
      setLoading(false)
    }
  }, [initialItems, statusFilter, sourceFilter, completedLsxOnly, showToast])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const materialMap = useMemo(() => {
    const map = new Map<string, MaterialStockOverviewItem>()
    for (const m of materials) {
      map.set(m.material_id, m)
    }
    return map
  }, [materials])

  // Filter items in-memory (supports both fetched items and initialItems)
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (statusFilter !== 'all' && item.status !== statusFilter) {
        return false
      }
      if (sourceFilter !== 'all') {
        if (sourceFilter === 'makeup') {
          const isMakeup = Boolean(item.makeup_request_id) || item.demand_type === 'PRODUCTION_MAKEUP'
          if (!isMakeup) return false
        } else if (sourceFilter === 'manual') {
          const isManual = item.demand_type === 'MANUAL_HOLD' || (!item.makeup_request_id && !item.demand_type)
          if (!isManual) return false
        } else if (sourceFilter === 'demand') {
          const isDemand = !item.makeup_request_id && item.demand_type !== 'MANUAL_HOLD' && (item.demand_type === 'PRODUCTION' || Boolean(item.plan_line_id))
          if (!isDemand) return false
        }
      }
      if (completedLsxOnly) {
        const remaining = item.remaining ?? Math.max(0, Number(item.qty_reserved_base) - Number(item.qty_consumed_base))
        if (!(item.status === 'ACTIVE' && remaining > 0 && item.plan_line_done === true)) {
          return false
        }
      }
      return true
    })
  }, [items, statusFilter, sourceFilter, completedLsxOnly])

  // Distinct list of plan lines for dropdown in hold modal
  const distinctPlanLines = useMemo(() => {
    const map = new Map<string, { id: string; label: string }>()
    for (const item of items) {
      if (item.plan_line_id && item.order_code) {
        map.set(item.plan_line_id, {
          id: item.plan_line_id,
          label: `${item.order_code} · ${item.customer_name || 'Khách'} · ${item.product_name || item.product_code || ''}`,
        })
      }
    }
    return Array.from(map.values())
  }, [items])

  // Start edit mode
  const handleStartEdit = () => {
    if (!canReservationsWrite) {
      showToast('Chỉ Kho và Mua hàng được chỉnh giữ hàng')
      return
    }
    const snap = JSON.parse(JSON.stringify(items))
    setEditSnapshot(snap)
    const initialDraft: Record<string, number> = {}
    items.forEach((it) => {
      initialDraft[it.id] = Number(it.qty_reserved_base)
    })
    setDraftQtys(initialDraft)
    setIsEditing(true)
  }

  // Cancel edit mode
  const handleCancelEdit = () => {
    if (editSnapshot) {
      setItems(editSnapshot)
    }
    setIsEditing(false)
    setEditSnapshot(null)
    setDraftQtys({})
    showToast('Đã hủy bỏ — thay đổi chưa xác nhận được hoàn tác')
  }

  // Changed items in edit mode
  const changedItems = useMemo(() => {
    if (!editSnapshot) return []
    return items
      .map((curr) => {
        const orig = editSnapshot.find((x) => x.id === curr.id)
        const newQ = draftQtys[curr.id] ?? Number(curr.qty_reserved_base)
        const oldQ = Number(orig?.qty_reserved_base || 0)
        return {
          item: curr,
          oldQ,
          newQ,
          diff: newQ - oldQ,
        }
      })
      .filter((x) => x.diff !== 0)
  }, [items, editSnapshot, draftQtys])

  // Confirm edit mode (open modal or save)
  const handleOpenConfirmModal = () => {
    if (changedItems.length === 0) {
      setIsEditing(false)
      setEditSnapshot(null)
      showToast('Không có thay đổi')
      return
    }
    setConfirmReason('')
    setConfirmModalOpen(true)
  }

  // Execute confirm edit
  const handleExecuteConfirmEdit = async () => {
    const reason = confirmReason.trim()
    if (!reason) {
      showToast('Bắt buộc nhập lý do')
      return
    }

    try {
      for (const ch of changedItems) {
        await updateReservationQtyApi(ch.item.id, {
          qty_base: ch.newQ,
          reason,
        })
      }
      showToast(`Đã chỉnh số lượng giữ ${changedItems.length} dòng`)
      setIsEditing(false)
      setEditSnapshot(null)
      setConfirmModalOpen(false)
      await loadData()
    } catch (err: any) {
      showToast(err.message || 'Lỗi khi cập nhật số lượng giữ')
    }
  }

  // Release reservation
  const handleRelease = useCallback(async (r: ReservationItem) => {
    if (!canReservationsWrite) {
      showToast('Chỉ Kho và Mua hàng được bỏ giữ')
      return
    }
    if (isEditing) {
      showToast('Đang ở chế độ sửa — Xác nhận hoặc Hủy bỏ trước')
      return
    }
    const remaining = r.remaining ?? Math.max(0, Number(r.qty_reserved_base) - Number(r.qty_consumed_base))
    const reason = window.prompt(
      `Lý do bỏ giữ ${fmtN(remaining)} ${r.base_uom} “${r.material_name}” (lệnh ${r.order_code || '—'}) — bắt buộc:`,
    )
    if (reason === null) return
    if (!reason.trim()) {
      showToast('Bắt buộc nhập lý do bỏ giữ')
      return
    }

    try {
      await releaseReservationApi(r.id, reason.trim())
      showToast(`Đã bỏ giữ ${fmtN(remaining)} ${r.base_uom} — tồn có thể dùng tăng lên`)
      await loadData()
    } catch (err: any) {
      showToast(err.message || 'Lỗi khi bỏ giữ')
    }
  }, [canReservationsWrite, isEditing, showToast, loadData])

  // Hold new
  const handleSaveHold = async () => {
    if (!holdMaterialId) {
      showToast('Vui lòng chọn vật tư')
      return
    }
    const q = Number(holdQty)
    if (!Number.isFinite(q) || q <= 0) {
      showToast('Số lượng giữ phải > 0')
      return
    }
    const mat = materialMap.get(holdMaterialId)
    const maxAvail = mat ? Math.max(0, mat.available_to_reserve ?? ((mat.physical_available ?? mat.available ?? 0) - (mat.reserved_base ?? 0))) : 0
    const uom = mat?.base_uom || mat?.uom_default || ''
    const matName = mat?.name || mat?.material_name || ''
    if (q > maxAvail) {
      showToast(`Không đủ tồn có thể dùng (còn ${fmtN(maxAvail)} ${uom})`)
      return
    }

    try {
      await createManualReservationApi({
        material_id: holdMaterialId,
        qty_base: q,
        plan_line_id: holdPlanLineId || undefined,
        note: holdNote.trim() || undefined,
      })
      showToast(`Đã giữ ${fmtN(q)} ${uom} “${matName}”`)
      setHoldModalOpen(false)
      await loadData()
    } catch (err: any) {
      showToast(err.message || 'Lỗi khi tạo giữ hàng')
    }
  }

  // Grid columns
  const cols: ExcelColumnDef<ReservationItem>[] = useMemo(() => [
    {
      key: 'id',
      header: 'Mã giữ',
      width: 110,
      getValue: (r) => r.id,
      renderCell: (r) => <span style={{ color: '#666', fontSize: 12 }}>{r.id.slice(0, 10)}</span>,
    },
    {
      key: 'material_name',
      header: 'Tên vật tư',
      width: 220,
      getValue: (r) => r.material_name,
    },
    {
      key: 'base_uom',
      header: 'ĐVT',
      width: 60,
      align: 'center',
      getValue: (r) => r.base_uom,
    },
    {
      key: 'order_code',
      header: 'Lệnh sản xuất',
      width: 140,
      align: 'center',
      getValue: (r) => r.order_code || '',
      renderCell: (r) => {
        if (!r.order_code) return <span style={{ color: '#888' }}>—</span>
        if (r.plan_line_id) {
          return (
            <Link
              to={`/planning/production-orders/${r.plan_line_id}/execution`}
              style={{ color: '#0b4fb3', fontWeight: 700, textDecoration: 'underline' }}
            >
              {r.order_code}
            </Link>
          )
        }
        return <b>{r.order_code}</b>
      },
    },
    {
      key: 'customer_name',
      header: 'Khách hàng',
      width: 170,
      getValue: (r) => r.customer_name || '—',
    },
    {
      key: 'customer_po',
      header: 'K/H-PO',
      width: 150,
      getValue: (r) => r.customer_po || '—',
    },
    {
      key: 'product_code',
      header: 'Mã hàng',
      width: 120,
      getValue: (r) => r.product_code || r.product_name || '—',
      renderCell: (r) => (
        <span>{r.product_name ? `${r.product_name} (${r.product_code || ''})` : r.product_code || '—'}</span>
      ),
    },
    {
      key: 'qty_reserved_base',
      header: 'Số lượng giữ',
      width: 100,
      isNumeric: true,
      getValue: (r) => draftQtys[r.id] ?? Number(r.qty_reserved_base),
      renderCell: (r) => {
        const val = draftQtys[r.id] ?? Number(r.qty_reserved_base)
        if (isEditing && r.status === 'ACTIVE' && canReservationsWrite) {
          const mat = materialMap.get(r.material_id)
          const avail = mat ? Math.max(0, mat.available_to_reserve ?? ((mat.physical_available ?? mat.available ?? 0) - (mat.reserved_base ?? 0))) : 0
          const minQ = Number(r.qty_consumed_base || 0)
          const maxQ = Number(r.qty_reserved_base) + avail
          return (
            <input
              type="number"
              min={minQ}
              max={maxQ}
              step="any"
              value={val}
              onChange={(e) => {
                const n = Number(e.target.value)
                setDraftQtys((prev) => ({ ...prev, [r.id]: n }))
              }}
              style={{
                width: '100%',
                border: '1px solid #8aa',
                padding: '2px 4px',
                textAlign: 'right',
                background: '#fff',
              }}
            />
          )
        }
        return fmtN(val)
      },
    },
    {
      key: 'qty_consumed_base',
      header: 'Đã xuất',
      width: 90,
      isNumeric: true,
      getValue: (r) => Number(r.qty_consumed_base),
      renderCell: (r) => fmtN(r.qty_consumed_base),
    },
    {
      key: 'remaining',
      header: 'Còn lại',
      width: 90,
      isNumeric: true,
      getValue: (r) => {
        if (r.status !== 'ACTIVE') return 0
        const q = draftQtys[r.id] ?? Number(r.qty_reserved_base)
        return Math.max(0, q - Number(r.qty_consumed_base))
      },
      renderCell: (r) => {
        if (r.status !== 'ACTIVE') return <span>0</span>
        const q = draftQtys[r.id] ?? Number(r.qty_reserved_base)
        const left = Math.max(0, q - Number(r.qty_consumed_base))
        return <b style={{ color: left > 0 ? '#1b6e2d' : '#888' }}>{fmtN(left)}</b>
      },
    },
    {
      key: 'status',
      header: 'Trạng thái',
      width: 230,
      getValue: (r) => {
        if (r.status === 'ACTIVE') return 'Đang giữ'
        if (r.status === 'CONSUMED') return 'Đã xuất'
        if (r.status === 'RELEASED') return 'Đã giải phóng'
        return r.status
      },
      renderCell: (r) => {
        const remaining = r.remaining ?? Math.max(0, Number(r.qty_reserved_base) - Number(r.qty_consumed_base))
        const isLsxDoneWarning = r.status === 'ACTIVE' && remaining > 0 && r.plan_line_done === true

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
            <div>
              {r.status === 'ACTIVE' && <span style={{ color: '#0b4fb3', fontWeight: 600 }}>Đang giữ</span>}
              {r.status === 'CONSUMED' && <span style={{ color: '#1b6e2d', fontWeight: 600 }}>Đã xuất</span>}
              {r.status === 'RELEASED' && <span style={{ color: '#888', textDecoration: 'line-through' }}>Đã giải phóng</span>}
            </div>
            {isLsxDoneWarning && (
              <StatusBadge tone="warning" label="LSX đã xong, cân nhắc bỏ giữ" />
            )}
          </div>
        )
      },
    },
    {
      key: 'source',
      header: 'Nguồn giữ',
      width: 140,
      getValue: (r) => {
        if (r.makeup_request_id) return 'Makeup bù'
        if (r.demand_type) return 'Nhu cầu SX'
        return 'Giữ tay (Kho)'
      },
      renderCell: (r) => {
        if (r.makeup_request_id) {
          return <span>Makeup bù {r.makeup_reason ? `(${r.makeup_reason})` : ''}</span>
        }
        if (r.demand_type) return <span>Nhu cầu SX</span>
        return <span>Giữ tay</span>
      },
    },
    {
      key: 'created_at',
      header: 'Ngày giữ',
      width: 100,
      align: 'center',
      getValue: (r) => formatDateTime(r.created_at),
      renderCell: (r) => <span title={formatDateTimeFull(r.created_at)}>{formatDateTime(r.created_at)}</span>,
    },
    {
      key: 'created_by',
      header: 'Người giữ',
      width: 130,
      getValue: (r) => r.created_by,
    },
    {
      key: 'release_reason',
      header: 'Lý do bỏ giữ',
      width: 180,
      getValue: (r) => r.release_reason || '',
    },
    {
      key: 'updated',
      header: 'Cập nhật cuối',
      width: 180,
      getValue: (r) => {
        if (r.last_change_at) {
          return `${formatDateTime(r.last_change_at)} · ${r.last_change_by || ''} (${r.last_change_reason || ''})`
        }
        return ''
      },
    },
    {
      key: 'actions',
      header: 'Thao tác',
      width: 90,
      align: 'center',
      getValue: () => '',
      renderCell: (r) => {
        if (r.status === 'ACTIVE' && canReservationsWrite && !isEditing) {
          return (
            <button
              onClick={() => void handleRelease(r)}
              style={{
                border: 'none',
                background: 'none',
                color: '#b00020',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Bỏ giữ
            </button>
          )
        }
        return null
      },
    },
  ], [draftQtys, isEditing, canReservationsWrite, materialMap, handleRelease])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, width: '100%', padding: '8px 12px', background: '#fff', boxSizing: 'border-box' }}>
      <Breadcrumb items={[{ label: 'Kho', to: '/warehouse/stock' }, { label: 'Giữ hàng' }]} />
      <PageHeader title="Giữ hàng" subtitle="Vật tư đang giữ cho lệnh sản xuất" />
      {/* Toast Notification */}
      {toastMessage && (
        <div
          style={{
            position: 'fixed',
            right: 14,
            bottom: 14,
            background: '#222',
            color: '#fff',
            padding: '8px 14px',
            borderRadius: 3,
            zIndex: 9999,
            fontSize: 13,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          {toastMessage}
        </div>
      )}

      {/* Header text for title */}
      <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px 0', display: 'none' }}>Giữ hàng</h2>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Tìm vật tư, lệnh SX, đơn hàng, khách…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: 250, padding: '4px 8px', border: '1px solid #aaa', borderRadius: 2 }}
        />

        <select
          data-testid="status-filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '4px 8px', border: '1px solid #aaa', borderRadius: 2, background: '#fff', fontSize: 13 }}
        >
          <option value="ACTIVE">Đang giữ</option>
          <option value="CONSUMED">Đã xuất hết</option>
          <option value="RELEASED">Đã bỏ giữ</option>
          <option value="all">Tất cả trạng thái</option>
        </select>

        <select
          data-testid="source-filter-select"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          style={{ padding: '4px 8px', border: '1px solid #aaa', borderRadius: 2, background: '#fff', fontSize: 13 }}
        >
          <option value="all">Tất cả nguồn giữ</option>
          <option value="demand">Nhu cầu SX</option>
          <option value="makeup">Makeup bù</option>
          <option value="manual">Giữ tay (Kho / Mua hàng)</option>
        </select>

        {completedLsxOnly && (
          <span
            data-testid="completed-lsx-indicator"
            style={{
              padding: '3px 8px',
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: 3,
              fontSize: 12,
              fontWeight: 600,
              color: '#92400e',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            Đang lọc: LSX đã hoàn thành
            <button
              type="button"
              onClick={() => setCompletedLsxOnly(false)}
              style={{ border: 'none', background: 'none', cursor: 'pointer', fontWeight: 700, color: '#92400e' }}
              title="Bỏ lọc LSX đã hoàn thành"
            >
              ✕
            </button>
          </span>
        )}

        <div style={{ flex: 1 }} />

        {isEditing ? (
          <>
            <button
              onClick={handleOpenConfirmModal}
              style={{
                padding: '5px 14px',
                background: '#1f6feb',
                color: '#fff',
                border: 'none',
                borderRadius: 3,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ✓ Xác nhận
            </button>
            <button
              onClick={handleCancelEdit}
              style={{
                padding: '5px 12px',
                background: '#f6f8fa',
                border: '1px solid #ccc',
                borderRadius: 3,
                cursor: 'pointer',
              }}
            >
              ✕ Hủy bỏ
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => {
                setHoldMaterialId('')
                setHoldPlanLineId('')
                setHoldQty('')
                setHoldNote('')
                setHoldModalOpen(true)
              }}
              disabled={!canReservationsWrite}
              style={{
                padding: '5px 12px',
                background: canReservationsWrite ? '#1f6feb' : '#999',
                color: '#fff',
                border: 'none',
                borderRadius: 3,
                fontWeight: 600,
                cursor: canReservationsWrite ? 'pointer' : 'not-allowed',
              }}
            >
              ＋ Giữ hàng
            </button>
            <button
              onClick={handleStartEdit}
              disabled={!canReservationsWrite}
              style={{
                padding: '5px 12px',
                background: canReservationsWrite ? '#f6f8fa' : '#eee',
                border: '1px solid #bbb',
                borderRadius: 3,
                fontWeight: 600,
                cursor: canReservationsWrite ? 'pointer' : 'not-allowed',
                color: canReservationsWrite ? '#222' : '#888',
              }}
            >
              ✎ Sửa số lượng giữ
            </button>
          </>
        )}

        <button
          onClick={() => {
            setSearchQuery('')
            setStatusFilter(initialItems ? 'all' : 'ACTIVE')
            setSourceFilter('all')
            setCompletedLsxOnly(false)
          }}
          disabled={!searchQuery && statusFilter === (initialItems ? 'all' : 'ACTIVE') && sourceFilter === 'all' && !completedLsxOnly}
          style={{
            padding: '5px 10px',
            background: '#f6f8fa',
            border: '1px solid #ccc',
            borderRadius: 3,
            cursor: (!searchQuery && statusFilter === (initialItems ? 'all' : 'ACTIVE') && sourceFilter === 'all' && !completedLsxOnly) ? 'default' : 'pointer',
            color: (!searchQuery && statusFilter === (initialItems ? 'all' : 'ACTIVE') && sourceFilter === 'all' && !completedLsxOnly) ? '#999' : '#333',
          }}
        >
          ✕ Bỏ lọc
        </button>

        <button
          onClick={() => setResetWidthsCounter((c) => c + 1)}
          title="Đặt lại độ rộng các cột về mặc định"
          style={{
            padding: '5px 10px',
            background: '#f6f8fa',
            border: '1px solid #ccc',
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          ↔ Đặt lại độ rộng
        </button>

        <button
          onClick={() => {
            const headers = cols.map((c) => getColHeader(c))
            const rows = filteredItems.map((r) => cols.map((c) => getColVal(c, r)))
            exportToCsv('giu-hang.csv', headers, rows)
            showToast('Đã xuất file CSV (mở được bằng Excel)')
          }}
          style={{
            padding: '5px 10px',
            background: '#f6f8fa',
            border: '1px solid #ccc',
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          ⬇ Excel
        </button>
      </div>

      {/* Grid Container */}
      <div style={{ flex: 1, minHeight: 0, border: '1px solid #ccc' }}>
        <ExcelGrid<ReservationItem>
          id="res"
          columns={cols}
          data={filteredItems}
          searchQuery={searchQuery}
          freeze={2}
          resetWidthsTrigger={resetWidthsCounter}
          isEditing={isEditing}
          rowCls={(r: ReservationItem) => (r.status === 'RELEASED' ? 'void' : undefined)}
          emptyMessage={loading ? 'Đang tải danh sách giữ hàng…' : 'Chưa có dòng giữ hàng nào'}
        />
      </div>

      {/* MODAL: Xác nhận chỉnh số lượng giữ */}
      {confirmModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 14,
          }}
        >
          <div
            style={{
              background: '#fff',
              border: '1px solid #777',
              width: 'min(760px, 98vw)',
              maxHeight: '94vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
            }}
          >
            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderBottom: '1px solid #bbb', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Xác nhận chỉnh số lượng giữ</span>
              <div style={{ flex: 1 }} />
              <button
                onClick={() => setConfirmModalOpen(false)}
                style={{ border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', padding: 4 }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '10px 14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ border: '1px solid #bbb', maxHeight: '40vh', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead style={{ background: '#f0f0f0' }}>
                    <tr>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', width: 40, textAlign: 'center' }}>STT</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'left' }}>Vật tư</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', width: 100, textAlign: 'center' }}>Lệnh SX</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', width: 90, textAlign: 'right' }}>SL giữ cũ</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', width: 90, textAlign: 'right' }}>SL giữ mới</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', width: 90, textAlign: 'right' }}>Chênh lệch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changedItems.map((ch, idx) => (
                      <tr key={ch.item.id}>
                        <td style={{ padding: '4px 8px', textAlign: 'center', background: '#f9f9f9', border: '1px solid #ddd' }}>{idx + 1}</td>
                        <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>{ch.item.material_name}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'center', border: '1px solid #ddd' }}>{ch.item.order_code || '—'}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ddd' }}>{fmtN(ch.oldQ)}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ddd' }}>{fmtN(ch.newQ)}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ddd', color: ch.diff > 0 ? '#1b6e2d' : '#b00020', fontWeight: 600 }}>
                          {ch.diff > 0 ? `+${fmtN(ch.diff)}` : fmtN(ch.diff)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <label style={{ fontSize: 12, color: '#b00020', fontWeight: 600, display: 'block', marginBottom: 2 }}>
                  Lý do chỉnh *
                </label>
                <textarea
                  rows={2}
                  placeholder="VD: Lệnh SX đổi số lượng, giữ thêm cho đợt 2…"
                  value={confirmReason}
                  onChange={(e) => setConfirmReason(e.target.value)}
                  style={{ width: '100%', border: '1px solid #c00', padding: '6px 8px', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderTop: '1px solid #bbb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setConfirmModalOpen(false)}
                style={{ padding: '5px 14px', background: '#fff', border: '1px solid #aaa', cursor: 'pointer' }}
              >
                Quay lại
              </button>
              <button
                onClick={() => void handleExecuteConfirmEdit()}
                style={{ padding: '5px 14px', background: '#1f6feb', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
              >
                Xác nhận chỉnh giữ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Giữ hàng cho lệnh sản xuất */}
      {holdModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 14,
          }}
        >
          <div
            style={{
              background: '#fff',
              border: '1px solid #777',
              width: 'min(860px, 98vw)',
              maxHeight: '94vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
            }}
          >
            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderBottom: '1px solid #bbb', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Giữ hàng cho lệnh sản xuất</span>
              <div style={{ flex: 1 }} />
              <button
                onClick={() => setHoldModalOpen(false)}
                style={{ border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', padding: 4 }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '10px 14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 2 }}>Vật tư *</label>
                  <MaterialTypeahead
                    materials={materials}
                    value={holdMaterialId}
                    onChange={(mId) => setHoldMaterialId(mId)}
                    placeholder="— Tìm hoặc chọn vật tư —"
                    clearable
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 2 }}>Giữ cho lệnh SX *</label>
                  <select
                    value={holdPlanLineId}
                    onChange={(e) => setHoldPlanLineId(e.target.value)}
                    style={{ width: '100%', border: '1px solid #999', padding: '6px 8px', background: '#fff' }}
                  >
                    <option value="">— Chọn lệnh SX —</option>
                    {distinctPlanLines.map((pl) => (
                      <option key={pl.id} value={pl.id}>
                        {pl.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 2 }}>Số lượng giữ *</label>
                  <input
                    type="number"
                    min={0.01}
                    step="any"
                    value={holdQty}
                    onChange={(e) => setHoldQty(e.target.value)}
                    style={{ width: '100%', border: '1px solid #999', padding: '6px 8px', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 2 }}>Ghi chú / lý do giữ</label>
                  <input
                    type="text"
                    placeholder="VD: Giữ trước cho đơn gấp…"
                    value={holdNote}
                    onChange={(e) => setHoldNote(e.target.value)}
                    style={{ width: '100%', border: '1px solid #999', padding: '6px 8px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ fontSize: 12, color: '#777', padding: '4px 0' }}>
                Chỉ giữ được trong số tồn có thể dùng. Giữ tay do Kho / Mua hàng ghi nhận, Kế hoạch thấy ở lệnh sản xuất.
              </div>
            </div>

            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderTop: '1px solid #bbb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setHoldModalOpen(false)}
                style={{ padding: '5px 14px', background: '#fff', border: '1px solid #aaa', cursor: 'pointer' }}
              >
                Hủy
              </button>
              <button
                onClick={() => void handleSaveHold()}
                style={{ padding: '5px 14px', background: '#1f6feb', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
              >
                Giữ hàng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
